import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import {
    DARK_CLASS,
    DARK_MEDIA_QUERY,
    normalizePreference,
    resolveTheme,
    THEME_STORAGE_KEY,
    type ThemePreference,
} from './theme';
import { ThemeContext, type ThemeContextValue } from './theme-context';

import type { ReactNode } from 'react';

/** Read the stored preference defensively — Safari throws here in private mode. */
function readStoredPreference(): ThemePreference {
    try {
        return normalizePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
        return 'system';
    }
}

/*
 * The OS preference as an external store rather than as `useState` + an effect.
 *
 * It genuinely is one: the value lives in the browser, changes without React's
 * involvement, and can flip between render and the effect that would have
 * subscribed to it. `useSyncExternalStore` closes that window by construction —
 * it reads through `getSnapshot` on every render, so there is no first render
 * holding a value that was already stale.
 */
function subscribeToSystemTheme(onChange: () => void): () => void {
    const query = window.matchMedia(DARK_MEDIA_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
}

function getSystemPrefersDark(): boolean {
    return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

/**
 * Owns the theme: the operator's stored preference, the OS preference it falls
 * back to, and the `dark` class on `<html>` that Tailwind keys every token off.
 *
 * It deliberately does **not** own the first paint. The inline script in
 * index.html has already put the right class on `<html>` before this component
 * — before the bundle was even fetched — so the effect below almost always
 * writes the class that is already there. That redundancy is the design: React
 * cannot run early enough to avoid a flash of the wrong theme, and a provider
 * that assumed it could would leave every cold load starting white and blinking
 * black. The script paints; this provider keeps it honest afterwards.
 *
 * State lives here rather than in each consumer so the switcher and the class on
 * `<html>` cannot disagree — the alternative, reading `classList` back out of
 * the DOM in the control, makes the DOM the source of truth for something React
 * is already re-rendering.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
    const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);

    /*
     * Subscribing at all is what makes `system` mean *system* and not
     * "whatever the system was when this tab opened". An operator whose OS
     * flips to dark at sunset should see this app flip with everything else,
     * and being the one internal tool that needs a reload to catch up is
     * exactly the kind of small wrongness nobody bothers to file.
     */
    const prefersDark = useSyncExternalStore(
        subscribeToSystemTheme,
        getSystemPrefersDark,
        // Server snapshot: this app never renders on a server, but the third
        // argument is what stops `useSyncExternalStore` reaching for
        // `window.matchMedia` if one is ever added.
        () => false,
    );

    const resolved = resolveTheme(preference, prefersDark);

    useEffect(() => {
        document.documentElement.classList.toggle(DARK_CLASS, resolved === 'dark');
    }, [resolved]);

    const setPreference = useCallback((next: ThemePreference) => {
        setPreferenceState(next);
        try {
            /*
             * `system` is stored explicitly rather than by removing the key.
             * Both behave identically today, but an absent key is also what
             * "never chosen" looks like, and keeping the two apart leaves room
             * for a later default that is not `system`.
             */
            window.localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch {
            // A blocked storage write costs the operator the preference on the
            // next load, not this session's theme. Nothing worth interrupting
            // them over.
        }
    }, []);

    const value = useMemo<ThemeContextValue>(
        () => ({ preference, resolved, setPreference }),
        [preference, resolved, setPreference],
    );

    return <ThemeContext value={value}>{children}</ThemeContext>;
}
